// v49.1 — Auth context + useShellUser hook.
// Single source of truth for "who is signed in" across the app. Wraps
// the Supabase session, derives the canonical ShellUser shape, and
// exposes the three mutation primitives (signIn, signUp, signOut).
//
// The shellUser shape mirrors what v48.x components expect to read from
// localStorage["lc_subscription"]: { name, email, role, company, mailId }.
// This means feature components migrated from v48 can keep reading the
// same fields — they just consume `useShellUser()` instead.

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { Session } from "@/lib/api/auth";

// Compat: User is just the session user shape
type User = Session["user"];
import { supabase, friendlyErrorMessage } from "@/lib/api/supabase-client";
import { fetchJsonSafe, setTokens, clearTokens, ApiError } from "@/lib/api/client";
import { fetchSessionContext, type SessionContext } from "@/lib/api/session";
import { store } from "@/lib/context/store";
import { hydrate as hydrateActing, reset as resetActing } from "@/lib/context/actingSlice";
import { LABOURCORE_API } from "@/lib/api";
import { bootstrapAccount } from "@/lib/api/identity";
import type { AdminTier, Profile, Role } from "@/lib/api/types";
import { shellSignOut } from "./helpers";
import { useCrossTabAuthSync } from "./useCrossTabAuthSync";
import { primeMatchModel } from "@/features/partners/matchModel";
import { primeMatchWeights } from "@/features/partners/matchWeights";

export interface ShellUser {
  id:          string;
  email:       string;
  name:        string;
  role:        Role;
  company:     string;
  companyId:   string | null;
  mailId:      string;
  initials:    string;
  gdprAccepted: boolean;
  avatarUrl?:  string | null;
  coverUrl?:   string | null;
  onboardingComplete: boolean;
  isAdmin: boolean;
  /* v50.321 — platform-admin tier. master = full panel (also isAdmin),
     board = view-only oversight, investor = revenue overview only.
     null for normal users. Legacy is_admin-without-role maps to master. */
  adminRole: AdminTier | null;
  /* v50.72 — SaaS tier for the user's company. Gates bulk-export and
     other Pro-only features. Admins always behave as Pro regardless
     (see useIsProTier). Single-record export remains free for all
     tiers per GDPR Art. 15(3). */
  subscriptionTier: "free" | "pro";
}

interface AuthState {
  /* "hydrate_error" — the session is real (token valid) but fetching the
     account's profile/company failed after a retry. Distinct from
     "authenticated" + companyId:null so TenantGate doesn't mistake a
     failed load for a brand-new signup — see hydrateFromSession. */
  status:        "loading" | "authenticated" | "anonymous" | "hydrate_error";
  user:          ShellUser | null;
  rawSession:    Session | null;
}

interface AuthContextValue extends AuthState {
  signIn: (email: string, password: string) => Promise<{ ok: boolean; error?: string; resetRequired?: boolean; resetToken?: string; resetUser?: { id: string; email: string } }>;
  signUp: (args: {
    email:     string;
    password:  string;
    name:      string;
    role:      Role;
    company:   string;
    extras?:   Record<string, unknown>;
  }) => Promise<{ ok: boolean; error?: string }>;
  signOut: () => void;
  /** M6: Complete a password reset and auto-login. */
  resetPassword: (resetToken: string, newPassword: string) => Promise<{ ok: boolean; error?: string }>;
  /** Request a forgot-password email. Always succeeds generically on the API. */
  requestPasswordReset: (email: string) => Promise<{ ok: boolean; error?: string }>;
  /** Retry hydration after a "hydrate_error" — used by HydrationErrorScreen. */
  retryHydration: () => Promise<void>;
}

/* The register wizard hands `extras` through as loose values, so the arrays
   are narrowed here rather than cast. An array of the wrong element type
   would be stripped by the endpoint's schema anyway — silently, which is
   exactly the kind of "saved successfully, saved nothing" this chunk is
   removing. */
function asStrings(v: unknown): string[] | undefined {
  return Array.isArray(v) && v.every(x => typeof x === "string") ? (v as string[]) : undefined;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * FALLBACK ONLY since chunk 5. The mail alias comes from the server with the
 * session context; this runs when that field is absent.
 *
 * It is worth knowing that this NEVER matched the server's value. The database
 * hashes `lc_v28_2026|role|email` through two accumulators and concatenates
 * the two halves; this hashes `email::role::lc-pepper-v1` through one and
 * repeats it. Two functions, two answers, one name — and the `role` they both
 * took no longer exists (migration 029), which is what forced the question.
 *
 * Kept so a hydration that loses the field still renders something stable
 * rather than blank. Do not build anything on it.
 */
function buildMailId(email: string, role: string): string {
  const s = `${email.toLowerCase()}::${role}::lc-pepper-v1`;
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  const hex = (h >>> 0).toString(16).padStart(8, "0");
  return "u_" + hex + hex;
}

function deriveInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map(p => p[0]!.toUpperCase())
    .slice(0, 2)
    .join("") || "?";
}

/**
 * Hydrate the ShellUser shape from a Supabase user + their profile row.
 *
 * WHERE `role` COMES FROM NOW — read this before touching it.
 *
 * It used to be `profile.role` — `profiles.role`, one marketplace side stored
 * against a PERSON. Migration 029 dropped that column, because somebody who
 * belongs to a coordinator and a partner has two answers and it had room for
 * one. Priya is a member of a hybrid and owner of a coordinator; there is no
 * value that column could have held that was right for both.
 *
 * A marketplace side is a fact about a BUSINESS, so `role` is now the ACTING
 * COMPANY'S KIND, taken from the memberships the server returned with the
 * session context — the same list the acting slice hydrates from, so the shell
 * and the acting context cannot disagree.
 *
 * This one line is why roughly fifty `user.role` call sites across the app did
 * not each need editing. They were all asking "which side of the marketplace
 * am I on", and they now get an answer that is true. Screens that need to
 * react to a company SWITCH should still use `useActingSides()`, which is
 * reactive; this value is resolved once per hydration.
 *
 * The "coordinator" fallback survives for an account with no membership at all
 * — mid-registration, or the six admin/investor logins that have none. It is a
 * default for a question that does not yet apply, not a guess at an answer.
 */
function buildShellUser(
  supaUser: User,
  profile: Profile | null,
  companyId: string | null,
  subscriptionTier: "free" | "pro" = "free",
  memberships: Array<{ company_id: string; kind: string }> = [],
  mailId: string | null = null,
): ShellUser {
  const email = supaUser.email || (profile?.email ?? "");
  const active = memberships.find(m => m.company_id === companyId) ?? memberships[0];
  const role  = (active?.kind ?? "coordinator") as Role;
  const name  = profile?.name || (typeof supaUser.user_metadata?.name === "string" ? supaUser.user_metadata.name : email);
  return {
    id:           supaUser.id,
    email,
    name,
    role,
    company:      profile?.company || "",
    companyId,
    mailId:       mailId ?? buildMailId(email, role),
    initials:     deriveInitials(name),
    gdprAccepted: true, /* the wizard's confirm step gates on it, so by the time we get a profile it's accepted */
    avatarUrl:    profile?.avatar_url || null,
    coverUrl:     profile?.cover_url  || null,
    onboardingComplete: profile?.onboarding_complete === true,
    isAdmin: profile?.is_admin === true,
    adminRole: profile?.admin_role ?? (profile?.is_admin === true ? "master" : null),
    subscriptionTier,
  };
}

const sleep = (ms: number) => new Promise(res => setTimeout(res, ms));

/* GH #38 — thrown by hydrateAttempt when the API tells us the session
   itself is invalid (refresh already failed server-side), as opposed to a
   transient network/timeout failure. The shim's query builder never
   throws for an API error response (see supabase-client.ts's _exec() —
   it catches ApiError and returns {data: null, error: {code, message}}
   instead of rejecting), so without this check a dead session silently
   looked identical to "new user, no profile/company yet" and rendered
   AccountSetupPrompt instead of sending the user back to sign-in. */
class SessionExpiredError extends Error {}

/**
 * One hydrate attempt — fetches profile + memberships, self-heals a
 * missing tenant, primes caches. Throws on timeout/query failure (or
 * SessionExpiredError on a dead session) so the caller (hydrateFromSession)
 * can decide whether to retry.
 */
async function hydrateAttempt(session: Session): Promise<ShellUser> {
  const userId = session.user.id;
  /* Race each query against a timeout. A Neon pooler cold-start can
     legitimately take a few seconds after being idle — 6s per attempt
     (with one retry, see hydrateFromSession) gives that room without
     leaving a genuinely-hung query stuck forever. */
  const timeout = <T,>(p: PromiseLike<T>, label: string): Promise<T> => {
    return Promise.race([
      Promise.resolve(p),
      new Promise<T>((_, rej) =>
        setTimeout(() => rej(new Error(`hydrate timeout: ${label}`)), 6000)),
    ]);
  };
  /* MIGRATED 2026-08-14 → GET /api/session/context
       server: labourcore-api/src/modules/session/{controller,service,repository}.ts
       client: src/lib/api/session.ts

     Kept commented, not deleted, until this has been through client review.
     Delete once the In Review batch covering sign-in has been signed off.

     Why it went: these two calls plus the tier fetch below were three
     browser-composed queries, and the tier one could not start until the
     membership query returned a company id. Every app load paid two
     sequential network waves before the first screen could paint.

     Verified before removal: the endpoint was compared against these exact
     three calls, over HTTP, for all 28 accounts — including the account
     holding four memberships, where the ordering decides which tenant you
     land in. Byte-identical.

  const [profileR, membershipsR] = await Promise.all([
    timeout(supabase.from("profiles").select("*").eq("id", userId).maybeSingle(), "profiles"),
    timeout(supabase.from("memberships").select("company_id").eq("user_id", userId).limit(1), "memberships"),
  ]);

  const profileError     = (profileR as { error?: { code?: string } })?.error?.code;
  const membershipsError = (membershipsR as { error?: { code?: string } })?.error?.code;
  if (profileError === "SESSION_EXPIRED" || membershipsError === "SESSION_EXPIRED") {
    throw new SessionExpiredError();
  }

  const profile     = (profileR as { data?: Profile | null })?.data ?? null;
  let memberships   = (membershipsR as { data?: Array<{ company_id: string }> | null })?.data ?? null;
  let companyId     = (memberships && memberships[0]?.company_id) || null;
  */

  let ctx: SessionContext;
  try {
    ctx = await timeout(fetchSessionContext(), "session-context");
  } catch (e) {
    /* A dead session has to stay distinguishable from a slow one — the caller
       retries the second and signs out on the first. authedFetch throws this
       once a token refresh has already failed, which is the same signal the
       per-query error code used to carry. */
    if (e instanceof ApiError && e.code === "SESSION_EXPIRED") {
      throw new SessionExpiredError();
    }
    throw e;
  }

  /* Hand the memberships to the acting-context store as soon as they land.
     This is the ONLY place they enter the client: they are derived from the
     token server-side, so the browser cannot ask what somebody else may act as.

     The store validates any previously stored selection against this list, so a
     membership revoked since the last visit falls back rather than pointing at
     a company the server will now refuse. */
  /* The whole spine payload, not just memberships. `actable_tiers` is what the
     slice reconciles the stored preference against — without it a revoked tier
     would survive in localStorage and keep being sent until the server refused
     it. */
  store.dispatch(hydrateActing({
    memberships:   ctx.memberships ?? [],
    tiers:         ctx.tiers ?? [],
    actable_tiers: ctx.actable_tiers ?? [],
    capabilities:  ctx.capabilities ?? [],
    /* A deployment property, re-read on every hydration and never persisted. */
    demo_mode:     ctx.demo_mode === true,
  }));

  const profile   = ctx.profile;
  let   companyId = ctx.company_id;

  /* v49.10.7 — self-heal missing tenant.
     Some accounts (e.g. signups that ran before the GoTrue lock fix)
     have a profile but no memberships row because the
     create_company_for_user RPC was blocked by the SDK lock during
     signup. The RPC is idempotent (re-uses an existing owner
     membership for the same name+kind) so it's safe to retry on
     every sign-in until it succeeds. */
  if (!companyId && profile?.company) {
    /* The kind used to come from profiles.role, which migration 029 dropped.
       There is nothing left to read it from at this point and nothing to
       infer it from either: this branch runs precisely when the account has
       NO membership, so there is no company whose kind could answer.

       'hybrid' is the honest default rather than the convenient one. A hybrid
       can act on both sides, so a wrongly-hybrid company leaves the user able
       to reach every screen and correct it in Settings; a wrongly-narrow one
       locks half the product away behind a value they cannot see or change.
       The onboarding wizard asks the question properly straight afterwards. */
    lcTrace("self-heal:start", { companyName: profile.company });
    const kind = "hybrid";
    try {
      const newCompanyId = await LABOURCORE_API.companies.createForUser(profile.company, kind);
      lcTrace("self-heal:done", { newCompanyId });
      if (newCompanyId) companyId = newCompanyId;
    } catch (healErr) {
      lcTrace("self-heal:failed", { error: (healErr as Error).message });
    }
  }

  if (companyId) LABOURCORE_API.setActiveCompanyId(companyId);

  /* v50.335 — Defence P9: prime the learned match-model cache once the
     viewer's company is known. Fire-and-forget — scoring falls back to
     the deterministic heuristic until (and unless) it resolves. */
  if (companyId) primeMatchModel(companyId);

  /* v50.336 — Defence P10: hydrate this account's saved match weights
     (match_preferences) into the local cache. Fire-and-forget; the
     device-local weights serve until (and unless) the row arrives. */
  primeMatchWeights(userId);

  /* v50.72 — the company's subscription_tier gates Pro-only features in the
     shell. It now arrives with the session context above rather than as its
     own round trip, and still fails closed: anything that isn't exactly 'pro'
     resolves to 'free', server-side.

     ORDERING NOTE. This used to run *after* the self-heal, so an account that
     had just had a company created for it got that company's tier. The
     context is now resolved before the heal, so a healed account reports the
     tier it had at read time — which is 'free', because companyId was null.
     That is correct rather than merely tolerable: companies.subscription_tier
     is NOT NULL DEFAULT 'free', so a company created moments ago is 'free' by
     definition. Checked against the live schema, not assumed. If that default
     ever changes, this needs a re-read after a successful heal.

     MIGRATED 2026-08-14 → GET /api/session/context (see the block above).

  let tier: "free" | "pro" = "free";
  if (companyId) {
    try {
      const tierR = await timeout(
        supabase.from("companies").select("subscription_tier").eq("id", companyId).maybeSingle(),
        "company-tier",
      );
      const raw = (tierR as { data?: { subscription_tier?: string } | null })?.data?.subscription_tier;
      if (raw === "pro") tier = "pro";
    } catch (e) {
      console.warn("[AuthContext] tier fetch failed (defaulting free):", e);
    }
  }
  */
  const tier: "free" | "pro" = ctx.subscription_tier;

  /* memberships carry each company's kind — that is what `role` is built
     from now. Same list the acting slice was hydrated with a few lines above,
     so the shell and the acting context cannot disagree about which side of
     the marketplace the user is on. */
  return buildShellUser(session.user, profile, companyId, tier, ctx.memberships ?? [], ctx.mail_id ?? null);
}

/** Discriminates the three real outcomes of a hydrate attempt — see
 * hydrateFromSession. Plain `ShellUser | null` couldn't tell "your session
 * is genuinely dead, sign in again" apart from "we couldn't check right
 * now, try again", which is exactly what made GH #38 possible. */
type HydrateOutcome =
  | { kind: "ok"; user: ShellUser }
  | { kind: "expired" }
  | { kind: "error" };

/**
 * v50.4xx — retry + distinct failure states.
 * A slow/failed hydrate (Neon pooler cold-start, network stall) used to
 * silently fall back to buildShellUser(session.user, null, null) — a
 * shape indistinguishable from "brand new signup, never finished
 * onboarding", which made AccountSetupPrompt ("Finish setting up your
 * account") appear over a perfectly real, already-onboarded account. One
 * retry gives a transient stall a second chance. A genuinely-expired
 * session (SessionExpiredError) is never retried — retrying a dead
 * refresh token can't succeed — and is reported distinctly from a
 * transient failure so the caller sends the user to sign-in instead of
 * offering a "try again" that will only fail the same way forever. */
async function hydrateFromSession(session: Session): Promise<HydrateOutcome> {
  for (let attempt = 1; attempt <= 2; attempt++) {
    try {
      const user = await hydrateAttempt(session);
      return { kind: "ok", user };
    } catch (e) {
      if (e instanceof SessionExpiredError) {
        lcTrace("hydrate:session-expired");
        return { kind: "expired" };
      }
      lcTrace(`hydrate:attempt-${attempt}-failed`, { error: (e as Error).message });
      if (attempt === 2) {
        console.warn("[AuthContext] hydrate failed after retry:", e);
        return { kind: "error" };
      }
      await sleep(750);
    }
  }
  return { kind: "error" };
}

/* v49.10.5 — auth trace ring buffer (accessible at window.__lcAuthTrace).
   Captures every auth lifecycle event so we can diagnose "token landed
   but status didn't flip" failures by pasting the trace in DevTools. */
declare global {
  interface Window { __lcAuthTrace?: Array<{ t: number; e: string; data?: unknown }>; }
}
function lcTrace(e: string, data?: unknown) {
  if (typeof window === "undefined") return;
  window.__lcAuthTrace = window.__lcAuthTrace || [];
  window.__lcAuthTrace.push({ t: Date.now(), e, data });
  if (window.__lcAuthTrace.length > 100) window.__lcAuthTrace.shift();
  console.log("[auth]", e, data ?? "");
}

/** Shared by every hydrateFromSession call site — turns a HydrateOutcome
 * into the right AuthState. "expired" clears the dead tokens and sends
 * the user to sign-in (GH #38); "error" is a transient failure and shows
 * the retry screen; "ok" is the normal authenticated path. */
function applyHydrateOutcome(
  outcome: HydrateOutcome,
  session: Session,
  setState: (s: AuthState) => void,
  label: string,
): void {
  if (outcome.kind === "ok") {
    setState({ status: "authenticated", user: outcome.user, rawSession: session });
    lcTrace(`state→authenticated (${label})`);
  } else if (outcome.kind === "expired") {
    clearTokens();
    setState({ status: "anonymous", user: null, rawSession: null });
    lcTrace(`state→anonymous (session expired, ${label})`);
  } else {
    setState({ status: "hydrate_error", user: null, rawSession: session });
    lcTrace(`state→hydrate_error (${label})`);
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: "loading", user: null, rawSession: null });

  /* On mount + on every auth-state-change, re-hydrate. */
  useEffect(() => {
    let cancelled = false;
    lcTrace("provider-mount");

    (async () => {
      lcTrace("getSession:start");
      const { data } = await supabase.auth.getSession();
      const session = data?.session || null;
      lcTrace("getSession:done", { hasSession: !!session, userId: session?.user?.id });
      if (cancelled) { lcTrace("getSession:cancelled"); return; }
      if (!session) {
        setState({ status: "anonymous", user: null, rawSession: null });
        lcTrace("state→anonymous (no initial session)");
        return;
      }
      lcTrace("initial-hydrate:start");
      const outcome = await hydrateFromSession(session);
      lcTrace("initial-hydrate:done", { kind: outcome.kind });
      if (!cancelled) applyHydrateOutcome(outcome, session, setState, "initial");
    })();

    /* v49.10.4 — defer DB hydrate to next tick.
       Supabase v2's GoTrue client holds an internal lock while
       onAuthStateChange callbacks run. Awaiting supabase.from(...)
       queries inside the callback can deadlock: the DB request waits
       on the lock that the callback itself is holding. Symptoms: token
       lands in localStorage after signIn, but status never flips to
       "authenticated" because hydrateFromSession never resolves.
       setTimeout(_, 0) lets the callback return, releases the lock,
       then the DB queries can proceed. */
    const { data: sub } = supabase.auth.onAuthStateChange((event: string, session: Session | null) => {
      lcTrace("onAuthStateChange", { event, hasSession: !!session, userId: session?.user?.id });
      if (cancelled) { lcTrace("onAuthStateChange:cancelled"); return; }
      /* GH #37/#39/#46 — the shim synthesizes an "INITIAL_SESSION" event
         on subscribe (see supabase-client.ts's onAuthStateChange) purely
         so real-Supabase-shaped consumers get an initial callback. The
         mount IIFE above already runs its own getSession()+hydrate for
         that exact initial case; reacting to INITIAL_SESSION here too
         fired a SECOND, fully independent hydrateFromSession call racing
         the first one, with whichever resolved last silently overwriting
         the other's setState — a real, reproducible source of the
         inconsistent/flickering auth state on launch, not just a timing
         fluke. */
      if (event === "INITIAL_SESSION") { lcTrace("onAuthStateChange:ignored-initial"); return; }
      if (event === "SIGNED_OUT" || !session) {
        setState({ status: "anonymous", user: null, rawSession: null });
        lcTrace("state→anonymous (event=" + event + ")");
        return;
      }
      setTimeout(async () => {
        if (cancelled) { lcTrace("deferred-hydrate:cancelled-before-start"); return; }
        lcTrace("deferred-hydrate:start");
        const outcome = await hydrateFromSession(session);
        lcTrace("deferred-hydrate:done", { kind: outcome.kind });
        if (!cancelled) {
          applyHydrateOutcome(outcome, session, setState, "signed-in");
        } else {
          lcTrace("deferred-hydrate:cancelled-after");
        }
      }, 0);
    });

    /* Listen for the legacy lc-auth-changed event too (fired by
       shellSignOut + any v48-era component that mutates auth state). */
    const onLegacy = () => {
      supabase.auth.getSession().then((res: { data: { session: Session | null } }) => {
        if (cancelled) return;
        if (!res?.data?.session) setState({ status: "anonymous", user: null, rawSession: null });
      });
    };
    /* BOTH spellings. `_notifyAuthChange` fires both, but client.ts's
       refresh-failure path historically fired only the colon form — so a
       genuinely dead session never reached this listener and the tab kept
       rendering the authenticated shell (GH #198, the mild half). client.ts now
       fires both as well; listening for both means neither side has to
       remember. */
    window.addEventListener("lc-auth-changed", onLegacy);
    window.addEventListener("lc:auth-changed", onLegacy);

    return () => {
      cancelled = true;
      sub?.subscription?.unsubscribe?.();
      window.removeEventListener("lc-auth-changed", onLegacy);
      window.removeEventListener("lc:auth-changed", onLegacy);
    };
  }, []);

  /* GH #198 / #207 / #209 — cross-tab session sync.
     Tokens are shared across tabs via localStorage and authedFetch re-reads
     them per request, so without this a tab can render one account while its
     requests execute as another. See useCrossTabAuthSync for why a foreign
     session forces a reload rather than a re-hydrate. */
  const onSessionCleared = useCallback(() => {
    setState({ status: "anonymous", user: null, rawSession: null });
  }, []);
  const onForeignSession = useCallback(() => {
    /* Reload rather than patch state: cached tenant data elsewhere in the tab
       would otherwise survive under the new identity. */
    try { window.location.reload(); } catch { /* non-browser env */ }
  }, []);
  useCrossTabAuthSync({
    currentUserId: state.user?.id ?? null,
    onSessionCleared,
    onForeignSession,
  });

  const value = useMemo<AuthContextValue>(() => ({
    ...state,
    async signIn(email, password) {
      try {
        const { data, error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) {
          // M6: detect reset_required from the shim's special error code
          if (error.message === "PASSWORD_RESET_REQUIRED" && data?.user) {
            const u = data.user as any;
            return {
              ok: false,
              resetRequired: true,
              resetToken: u.__reset_token,
              resetUser: { id: u.id, email: u.email },
            };
          }
          return { ok: false, error: error.message };
        }
        if (!data?.user) return { ok: false, error: "Sign-in returned no user" };
        return { ok: true };
      } catch (e) {
        return { ok: false, error: (e as Error).message };
      }
    },
    async requestPasswordReset(email: string) {
      const BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || "";
      const { ok, json, transportError } = await fetchJsonSafe<any>(`${BASE_URL}/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      if (transportError) return { ok: false, error: transportError };
      // API always returns generic 200 when valid; treat network/5xx as soft fail.
      if (!ok) return { ok: false, error: friendlyErrorMessage(json, "Something went wrong. Please try again.") };
      return { ok: true };
    },
    async resetPassword(resetToken: string, newPassword: string) {
      const BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || "";
      const { ok, json, transportError } = await fetchJsonSafe<any>(`${BASE_URL}/auth/reset-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${resetToken}`,
        },
        body: JSON.stringify({ new_password: newPassword }),
      });
      if (transportError) return { ok: false, error: transportError };
      if (!ok) return { ok: false, error: friendlyErrorMessage(json, "Password reset failed. Please try again.") };

      /* v50.436 — #138: don't auto-login on reset. A session created purely
         from clicking a reset-password email link (no credential entry) is
         a weaker security posture than requiring an explicit sign-in after
         a credential change. The reset endpoint may still return tokens for
         backward compat; deliberately ignored here. */
      return { ok: true };
    },
    async signUp({ email, password, name, role, company, extras }) {
      try {
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: { data: { name, role, company } },
        });
        if (error) return { ok: false, error: error.message };
        const supaUser = data?.user;
        if (!supaUser) return { ok: false, error: "Sign-up returned no user" };

        /*
         * v51 (chunk 5) — the profile row and the consent record are one
         * call, POST /api/identity/bootstrap, in one transaction.
         *
         * They used to be two independent proxy writes, each wrapped in its
         * own try/catch that swallowed failures — which is how a signup could
         * report success having recorded neither. The comment on the first
         * one blamed "a transient RLS issue"; RLS is disabled on `profiles`,
         * so whatever was failing, that was not it.
         *
         * `role` IS NOT SENT. It went to profiles.role, a marketplace side
         * stored against a PERSON, which is where the broken mode toggle
         * came from. The company's kind carries that fact and the company is
         * created immediately below.
         */
        try {
          const ex = extras || {};
          await bootstrapAccount({
            email,
            name,
            company,
            title:    role === "coordinator" ? "MEP Project Manager" : "",
            location: "London",
            /* #164 — the consent this replaces named consent_kind and
               policy_version, columns that do not exist on gdpr_consents. It
               threw on every single signup and was swallowed, so no consent
               has ever been recorded. It is part of the same transaction as
               the profile now, and the IP comes from the request rather than
               from here. */
            consent_scope:      "data_processing",
            trades:             asStrings(ex.trades),
            regions:            asStrings(ex.regions),
            declared_certs:     asStrings(ex.declaredCerts),
            project_types:      asStrings(ex.projectTypes),
            crew_size:          ex.teamSize          != null ? Number(ex.teamSize)          : undefined,
            typical_day_rate:   ex.typicalDayRate    != null ? Number(ex.typicalDayRate)    : undefined,
            typical_headcount:  ex.typicalHeadcount  != null ? Number(ex.typicalHeadcount)  : undefined,
            /* `extras` is loosely typed at the wizard boundary, so this one is
               narrowed rather than trusted — the endpoint takes a string. */
            procurement_system: typeof ex.procurementSystem === "string" && ex.procurementSystem
              ? ex.procurementSystem : undefined,
          });
        } catch (profileErr) {
          /* Still non-fatal, for the same reason it always was: the company
             RPC below is what TenantGate actually checks, and failing here
             would strand a user who has an auth account but no profile. It
             is logged loudly rather than silently, and it is now ONE thing
             that can fail rather than two. */
          console.warn("[signUp] identity bootstrap failed (non-fatal):", profileErr);
        }

        /* v48.274 — atomic company + owner-membership RPC.
           v50.169 fix (#86) — this used to be swallowed like the other
           steps below, so signUp() returned {ok:true} even when it
           silently failed. Since companyId (read from the membership
           this RPC creates) is the ONLY thing TenantGate checks to decide
           whether to show AccountSetupPrompt, a failure here was exactly
           why a fully-completed Register flow could still dead-end on
           "Finish setting up your account" re-asking for Name/Company/
           Role that were already collected. Now fatal so RegisterWizard
           surfaces a real error and the user can retry immediately,
           instead of discovering it later via the re-ask screen. */
        const kind = role === "coordinator" ? "coordinator" : role === "partner" ? "partner" : "hybrid";
        let newCompanyId: string | null = null;
        try {
          newCompanyId = await LABOURCORE_API.companies.createForUser(company, kind);
        } catch (coErr) {
          console.warn("[signUp] company RPC failed:", coErr);
        }
        if (!newCompanyId) {
          return { ok: false, error: "Couldn't finish setting up your company. Please try again." };
        }
        console.log("[signUp] tenant created:", newCompanyId, "kind:", kind);

        /* The gdpr_consents insert that stood here is now part of the
           bootstrap call above, in the same transaction as the profile it
           belongs to. #164 is what it was: it named consent_kind and
           policy_version, columns gdpr_consents does not have, so it threw on
           every signup and was swallowed — no consent has ever been written.
           A consent record is the artefact you produce when somebody asks
           whether a user agreed, so leaving its shape to a browser that
           cannot be told it got the column names wrong is how you end up with
           none of them. */

        return { ok: true };
      } catch (e) {
        return { ok: false, error: (e as Error).message };
      }
    },
    signOut: shellSignOut,
    async retryHydration() {
      if (!state.rawSession) return;
      lcTrace("retryHydration:start");
      const outcome = await hydrateFromSession(state.rawSession);
      applyHydrateOutcome(outcome, state.rawSession, setState, "manual retry");
    },
  }), [state]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

/* Most components just want the user object, not the full mutation surface. */
export function useShellUser(): ShellUser | null {
  return useAuth().user;
}
